import { Box, Button, Flex, IconButton, useDisclosure } from '@chakra-ui/react';
import React from 'react';

import { Icon } from '~/packages/common-frontend/components/icons';
import { useExpandedStore } from '~/packages/common-frontend/hooks/useExpandedStore';
import { ColorScheme, Sizes } from '~/packages/common-frontend/theme';
import { ButtonVariant } from '~/packages/common-frontend/theme/components/Button';

// Exports
type MenuTreeItemProps = {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: Sizes;
  variant?: ButtonVariant;
  colorScheme?: ColorScheme;
  defaultIsOpen?: boolean;
  expandedStoreKey?: string;
  fullSurfaceExpandsContent?: boolean;
  expandOffsetPadding?: string;
  overflow?: 'ellipsis' | 'multi-line';

  // Passthrough props
  ButtonProps?: React.ComponentProps<typeof Button>;
  ExpandIconButtonProps?: React.ComponentProps<typeof IconButton>;
  OuterContainerFlexProps?: React.ComponentProps<typeof Flex>;
  InnerContainerFlexProps?: React.ComponentProps<typeof Flex>;
} & ({ label: string } | { label?: React.ReactNode; 'aria-label': string });
export const MenuTreeItem = React.forwardRef<
  HTMLButtonElement,
  MenuTreeItemProps
>((props, ref) => {
  const {
    children,
    icon,
    isActive = false,
    isDisabled = false,
    isLoading = false,
    size = 'md',
    variant = 'v2-ghost',
    colorScheme = 'v2-aurora',
    defaultIsOpen = true,
    expandedStoreKey,
    fullSurfaceExpandsContent = true,
    expandOffsetPadding = '0.75rem',
    overflow = 'ellipsis',
    ButtonProps,
    ExpandIconButtonProps,
    OuterContainerFlexProps,
    InnerContainerFlexProps,
    ...rest
  } = props;

  // Store nested list expanded state preference in session storage
  const nestedExpandedStore = useExpandedStore({
    key: expandedStoreKey ? `MenuTreeItem.${expandedStoreKey}` : undefined,
    defaultTo: defaultIsOpen ? 'expanded' : 'collapsed',
  });
  const nestedListControls = useDisclosure({
    defaultIsOpen: nestedExpandedStore.preference === 'expanded',
  });

  const expandedIcon = nestedListControls.isOpen ? (
    <Icon icon="chevron-up" />
  ) : (
    <Icon icon="chevron-down" />
  );

  const overflowProps = {
    ellipsis: {
      button: { padding: '0 0.75rem' },
      label: {
        flexGrow: 1,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        textAlign: 'start',
        display: 'inline-block',
      },
    },
    'multi-line': {
      button: {
        height: 'fit-content',
        padding: '0.5rem 0.75rem',
        alignItems: 'baseline',
      },
      label: {
        width: '100%',
        whiteSpace: 'wrap',
        textAlign: 'start',
      },
    },
  } as const;

  return (
    <Flex direction="column" gap="0.25rem" {...OuterContainerFlexProps}>
      <Button
        ref={ref}
        variant={variant}
        colorScheme={colorScheme}
        size={size}
        aria-label={'aria-label' in rest ? rest['aria-label'] : rest.label}
        gap="0.75rem"
        justifyContent="flex-start"
        isActive={isActive}
        isDisabled={isDisabled}
        isLoading={isLoading}
        fontWeight="normal"
        {...overflowProps[overflow].button}
        {...ButtonProps}
        onClick={(event) => {
          if (isDisabled) event.preventDefault();
          if (children && fullSurfaceExpandsContent) {
            if (nestedListControls.isOpen) {
              nestedExpandedStore.set('collapsed');
              nestedListControls.onClose();
            } else {
              nestedExpandedStore.set('expanded');
              nestedListControls.onOpen();
            }
          } else {
            ButtonProps?.onClick?.(event);
          }
        }}
      >
        {/*
          !TODO @eldarshamukhamedov We probably need a wrapper around chakra/Button that handles the
          left and right icon slots explicitly. It should allow icon buttons in the slots as well.
           */}
        {icon && (
          <Box as="span" transform="translateY(-0.125rem)">
            {icon}
          </Box>
        )}
        <Box as="span" {...overflowProps[overflow].label}>
          {rest.label}
        </Box>

        {children &&
          !isDisabled &&
          (fullSurfaceExpandsContent ? (
            expandedIcon
          ) : (
            <IconButton
              variant={variant.includes('secondary') ? 'v2-ghost' : variant}
              colorScheme={colorScheme}
              minWidth="1.5rem"
              width="1.5rem"
              minHeight="1.5rem"
              height="1.5rem"
              icon={expandedIcon}
              aria-label="Expand"
              {...ExpandIconButtonProps}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (nestedListControls.isOpen) {
                  nestedExpandedStore.set('collapsed');
                  nestedListControls.onClose();
                } else {
                  nestedExpandedStore.set('expanded');
                  nestedListControls.onOpen();
                }
                ExpandIconButtonProps?.onClick?.(event);
              }}
            />
          ))}
      </Button>

      {children && !isDisabled && nestedListControls.isOpen && (
        <Flex
          direction="column"
          paddingLeft={expandOffsetPadding}
          gap="0.25rem"
          {...InnerContainerFlexProps}
        >
          {children}
        </Flex>
      )}
    </Flex>
  );
});

type MenuTreeProps = {
  children: React.ReactNode;
  FlexProps?: React.ComponentProps<typeof Flex>;
};
export const MenuTree = ({ children, FlexProps }: MenuTreeProps) => (
  <Flex
    direction="column"
    gap="0.25rem"
    flexGrow={1}
    overflowY="auto"
    style={{ scrollbarWidth: 'thin' }}
    {...FlexProps}
  >
    {children}
  </Flex>
);
